import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Image as ImageIcon, Upload, RotateCcw } from 'lucide-react';

const SOURCE_LABEL = {
  override: 'Manual override for this platform',
  'linked-default': "Linked from this video/clip's default Thumbnail (Thumbnails tool)",
  'server-source': "This video's saved thumbnail",
  none: 'No thumbnail yet',
};

/**
 * Optimize → Thumbnails: resolves in the exact priority order from the
 * spec (section 12) — explicit per-platform override, then the linked
 * default thumbnail from `lib/thumbnailAssociations.js`, then the
 * content source's real server-persisted thumbnail
 * (`/api/admin/content-sources/{id}/thumbnail`), then nothing. The
 * actual resolution happens in `OptimizeWorkspace.jsx` (needs an async
 * fetch for the server fallback); this panel only displays the result
 * and lets the creator override or clear it.
 */
export function OptimizeThumbnailPanel({ resolved, onSetOverride, onClearOverride, disabled }) {
  const fileInputRef = useRef(null);

  if (disabled) return <p className="text-xs text-[#8A93A6] text-center py-6" data-testid="optimize-thumbnail-empty">Select content and a platform first.</p>;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => onSetOverride(reader.result.toString());
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-3" data-testid="optimize-thumbnail-panel">
      <div className="aspect-video rounded-lg border border-white/10 bg-black/40 flex items-center justify-center overflow-hidden" data-testid="optimize-thumbnail-preview">
        {resolved.url ? <img src={resolved.url} alt="Thumbnail" className="w-full h-full object-cover" /> : <ImageIcon className="h-6 w-6 text-[#555E6E]" />}
      </div>
      <p className="text-[11px] text-[#8A93A6]" data-testid="optimize-thumbnail-source-label">{SOURCE_LABEL[resolved.source] || SOURCE_LABEL.none}</p>

      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="optimize-thumbnail-file-input" />
      <Button size="sm" variant="outline" className="w-full border-white/10" onClick={() => fileInputRef.current?.click()} data-testid="optimize-thumbnail-override-button">
        <Upload className="h-3.5 w-3.5 mr-1.5" />{resolved.source === 'override' ? 'Replace override' : 'Set a different thumbnail for this platform'}
      </Button>
      {resolved.source === 'override' && (
        <Button size="sm" variant="ghost" className="w-full text-xs text-[#8A93A6] hover:text-white" onClick={onClearOverride} data-testid="optimize-thumbnail-clear-override-button">
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />Clear override — use the linked default instead
        </Button>
      )}
    </div>
  );
}
